import React from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import Icon from "components/atoms/Icon";
import { IconName } from "components/atoms/Icon/IconNames";
import { PrescriptionGroup, PrescriptionHospitalGroup } from "common/models/types";
import PrescriptionGroupCard from "./PrescriptionGroupCard";
import PrescriptionHospitalGroupCard from "./PrescriptionHospitalGroupCard";

interface Prescription {
  medicationName: string;
  dosagePerAdministration: string;
  dosageUnit: string;
  timesPerDay: number;
  daysOfAdministration: number;
  usageInstruction?: string;
}

interface PrescriptionHistoryProps {
  prescriptionGroups: PrescriptionGroup[];
  hospitalGroups?: PrescriptionHospitalGroup[] | null;
  referenceChecker?: {
    [id: string]: {
      count: number;
      totalLength?: number;
      totalBytes?: number;
    };
  };
  onEditPrescription?: (groupId: string) => void;
  onDeletePrescription?: (groupId: string) => void;
  /** 읽기전용(보고서화면): 그룹 카드의 수정/삭제 버튼 미렌더 */
  readOnly?: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PrescriptionCard = styled.div`
  border: 1px solid ${COLOR_SYSTEM.get("Gray")[200]};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const PrescriptionHeader = styled.div`
  background: ${COLOR_SYSTEM.get("Gray")[50]};
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PrescriptionContent = styled.div`
  padding: 16px;
  background: white;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MedicationItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[10]};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ hoverColor?: string }>`
  padding: 4px;
  border-radius: 4px;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")[200]};
  background: white;
  color: ${COLOR_SYSTEM.get("Gray")[500]};
  transition: all 0.2s;

  &:hover {
    color: ${(props) => props.hoverColor || COLOR_SYSTEM.get("Gray")[700]};
    border-color: ${(props) => props.hoverColor || COLOR_SYSTEM.get("Skyblue")[300]};
  }
`;

function formatKoreanDate(dateInput?: string | Date) {
  if (!dateInput) return "-";
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  if (isNaN(date.getTime())) return String(dateInput);
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, "0")}.${String(
    date.getDate()
  ).padStart(2, "0")}`;
}

const PrescriptionHistory: React.FC<PrescriptionHistoryProps> = ({
  prescriptionGroups,
  hospitalGroups,
  referenceChecker,
  onEditPrescription,
  onDeletePrescription,
  readOnly,
}) => {
  return (
    <Container>
      {hospitalGroups ? (
        // 병원별 보기
        hospitalGroups.map((hospitalGroup) => (
          <PrescriptionHospitalGroupCard
            key={hospitalGroup.hospital}
            hospitalGroup={hospitalGroup}
            referenceChecker={referenceChecker}
            onEdit={onEditPrescription}
            onDelete={onDeletePrescription}
            readOnly={readOnly}
          />
        ))
      ) : (
        // 날짜별 보기
        prescriptionGroups.map((group) => (
          <PrescriptionGroupCard
            key={group.groupId}
            group={group}
            referenceChecker={referenceChecker}
            onEdit={onEditPrescription}
            onDelete={onDeletePrescription}
            readOnly={readOnly}
          />
        ))
      )}
    </Container>
  );
};

export default PrescriptionHistory;
